import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map } from "rxjs";
import { ApiResponse } from "shared/interfaces/response";
import { PaymentMethod } from "shared/models/settings/payment-method";
import { User } from "shared/models/users/user";
import { CreatePaymentMethodResponseDTO } from './dtos/create-payment-method-response.dto';
import { MakePaymentMethodDefaultRequestDTO } from "./dtos/make-payment-method-default-request.dto";
import { MakePaymentMethodDefaultResponseDTO } from "./dtos/make-payment-method-default-response.dto";
import { CancelSubscriptionResponseDTO } from "./dtos/cancel-subscription-response.dto";
import { PaymentHistory } from "shared/models/settings/payment-history";
import { MySubscriptionResponseDTO } from "./dtos/my-subscription-request.dto";
import { Query } from "shared/types/query";
import { ChangeSubscriptionResponseDTO } from "./dtos/change-subscription-response.dto";
import { DownloadPaymentInvoiceResponse } from "./dtos/download-payment-invoice-response.dto";
import { ModifyLimitResponseDTO } from "./dtos/modify-limit-response.dto";
import { AddOnDTO } from "./dtos/add-ons-response.dto";
import { ChangeSubscriptionPlanRequestDTO } from "./dtos/change-subscription-plan-request.dto";
import { TermsAndConditionsResponseDTO } from "./dtos/terms-and-conditions-response.dto";
import { AnyCatcher } from "rxjs/internal/AnyCatcher";
import { AddOnsCalcRequestDTO } from "./dtos/add-ons-calc-request.dto";
import { AddOnsCalcResponseDTO } from "./dtos/add-ons-calc-response.dto";
import { UpgradeLimitRequestDTO } from "./dtos/upgrade-limits-request.dto";
import { DowngradeLimitRequestDTO } from "./dtos/downgrade-limits-request.dto";
import { ToggleLowStockUpdateEmailPermissionRequestDTO } from "./dtos/toggle-low-stock-update-email-permission-request.dto";
import { ToggleLowStockUpdateEmailPermissionResponseDTO } from "./dtos/toggle-low-stock-update-email-permission-response.dto";
import { ScheduledExpiriesResponseDTO } from "./dtos/scheduled-expiries-response.dto";

@Injectable()

export class SettingsService {

    constructor(private http: HttpClient) { }

    updateProfile(body: FormData) {
        return this.http.patch<ApiResponse<User>>(`users/profile`, body).pipe(
            map(resp => ({ ...resp, data: new User(resp.data) }))
        )
    }

    getPaymentMethods() {
        return this.http.get<ApiResponse<PaymentMethod[]>>(`subscriptions/payment-methods`).pipe(
            map(resp => ({ ...resp, data: resp.data.map(i => new PaymentMethod(i)) }))
        )
    }

    getScheduledExpiries() {
        return this.http.get<ApiResponse<ScheduledExpiriesResponseDTO>>(`subscriptions/scheduled-expiries`).pipe(
            map(resp => ({ ...resp, data: new ScheduledExpiriesResponseDTO(resp.data) }))
        )
    }

    createPaymentMethod() {
        return this.http.post<ApiResponse<CreatePaymentMethodResponseDTO>>(`subscriptions/payment-methods/setup-intent`, {}).pipe(
            map(resp => ({ ...resp, data: new CreatePaymentMethodResponseDTO(resp.data) }))
        )
    }

    deletePaymentMethod(paymentMethodId: string) {
        return this.http.delete<ApiResponse<CreatePaymentMethodResponseDTO>>(`subscriptions/payment-methods/${paymentMethodId}`).pipe(
            map(resp => ({ ...resp, data: resp.data }))
        )
    }

    makePaymentMethodDefault(body: MakePaymentMethodDefaultRequestDTO){
        return this.http.patch<ApiResponse<MakePaymentMethodDefaultResponseDTO>>(`subscriptions/payment-methods/default`, body).pipe(
            map(resp => ({...resp, data: new MakePaymentMethodDefaultResponseDTO(resp.data)}))
        )
    }

    cancelSubscription(companySubscriptionId: string){
        return this.http.post<ApiResponse<CancelSubscriptionResponseDTO>>(`subscriptions/cancel/${companySubscriptionId}`, {}).pipe(
            map(resp => ({...resp, data: new CancelSubscriptionResponseDTO(resp.data)}))
        )
    }

    reactivateSubscription(companySubscriptionId: string){
        return this.http.post<ApiResponse<any>>(`subscriptions/reactivate/${companySubscriptionId}`, {}).pipe(
            map(resp => ({...resp, data: resp.data}))
        )
    }
    getPaymentHistory(params: Query) {
        return this.http.get<ApiResponse<PaymentHistory[]>>(`billing-history`, { params }).pipe(
            map(resp => ({ ...resp, data: resp.data.map(i => new PaymentHistory(i)), paginator: resp.paginator }))
        )
    }

    changeSubscription(subscriptionId: string, body: ChangeSubscriptionPlanRequestDTO){
        return this.http.post<ApiResponse<ChangeSubscriptionResponseDTO>>(`subscriptions/update/${subscriptionId}`, body).pipe(
            map(resp => ({...resp, data: new ChangeSubscriptionResponseDTO(resp.data)}))
        )
    }

    getMySubscription(){
        return this.http.get<ApiResponse<MySubscriptionResponseDTO>>(`subscriptions/my-subscriptions`).pipe(
            map(resp => ({...resp, data: new MySubscriptionResponseDTO(resp.data)}))
        )
    }

    downloadPaymentInvoice(params: Query){
        return this.http.get<ApiResponse<DownloadPaymentInvoiceResponse>>(`companies/invoices/download`, { params }).pipe(
            map(resp => ({...resp, data: resp.data}))
        )
    }

    upgradeSubscriptionLimit(companySubscriptionId: string, body: UpgradeLimitRequestDTO){
        return this.http.post<ApiResponse<ModifyLimitResponseDTO>>(`subscriptions/upgrade/${companySubscriptionId}`, body).pipe(
            map(resp => ({...resp, data: new ModifyLimitResponseDTO(resp.data)}))
        )
    }

    downgradeSubscriptionLimit(companySubscriptionId: string, body: DowngradeLimitRequestDTO){
        return this.http.post<ApiResponse<ModifyLimitResponseDTO>>(`subscriptions/downgrade/${companySubscriptionId}`, body).pipe(
            map(resp => ({...resp, data: new ModifyLimitResponseDTO(resp.data)}))
        )
    }

    getSubscriptionAddons(){
        return this.http.get<ApiResponse<AddOnDTO[]>>(`subscriptions/addons`).pipe(
            map(resp => ({...resp, data: resp.data.map(i => new AddOnDTO(i))}))
        )
    }

    getTermsAndConditions(){
        return this.http.get<ApiResponse<TermsAndConditionsResponseDTO>>(`privacy-policy/all`).pipe(
            map(resp => ({...resp, data: new TermsAndConditionsResponseDTO(resp.data)}))
        )
    }

    getAddOnsCalculation(companySubscriptionId: string, body: AddOnsCalcRequestDTO){
        return this.http.post<ApiResponse<AddOnsCalcResponseDTO>>(`subscriptions/upgrade/${companySubscriptionId}/preview-charge`, body).pipe(
            map(resp => ({...resp, data: new AddOnsCalcResponseDTO(resp.data)}))
        )
    }

    toggleLowStockUpdateEmailPermission(body: ToggleLowStockUpdateEmailPermissionRequestDTO){
        return this.http.patch<ApiResponse<ToggleLowStockUpdateEmailPermissionResponseDTO>>(`companies/me/low-stock-email`, body).pipe(
            map(resp => ({...resp, data: new ToggleLowStockUpdateEmailPermissionResponseDTO(resp.data)}))
        )
    }
}
